// QC dashboard — the workspace the dev team lives in (Chauncey, 2026-10-15:
// "I live in the Dashboards page the most").
//
// One screen, no page jumps: the queue on the left, the selected case docked
// on the right with everything needed to service it (components/CaseView).
// Clicking a row swaps the pane in place; ↑/↓ walk the queue; Esc closes.
// The first case in the active list is selected on arrival so the screen
// opens on work, not on an empty pane. Below 1100px a row click goes to the
// full case page as before.
//
// Two LANES (bugs vs feature requests — never one queue, Chauncey 2026-07-01)
// scope the whole surface. Within a lane the counted pills are ACTIONS, not
// statistics: Needs your reply / To review, Accepted (features), In progress,
// Awaiting verify, Closed (kept so an accidental close stays visible).
// Module chips scope everything to one module; search narrows the list.
//
// Speed: every hop behind this page measures ~200 ms but they run in sequence
// behind the sign-in token, so the last board this account saw is painted
// from a localStorage snapshot on the first frame and replaced by the live
// payload when it lands (lib/snapshot.ts). The docked case paints from its
// list row before its detail arrives.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useMsal } from '@azure/msal-react';
import { signedInAccount } from '../auth/ssoMemory';
import type { QcCase, QcDashboard, QcModule, QcPeopleMap } from '@itms-qc/shared';
import { QC_TERMINAL_STATUSES } from '@itms-qc/shared';
import { api } from '../lib/api';
import { useApiToken } from '../auth/useApiToken';
import { useMe } from '../lib/me';
import { conversationCourt, CourtChip, ModuleBadge, StatusPill, TypeIcon, moduleColors } from '../components/badges';
import { CaseView } from '../components/CaseView';
import { NewCaseModal } from '../components/NewCaseModal';
import { personLabel, relativeTime } from '../lib/format';
import { isUnread } from '../lib/seen';
import { readSnapshot, writeSnapshot } from '../lib/snapshot';
import { useCanDock } from '../lib/useMediaQuery';
import { primaryButtonStyle } from '../components/form';

const MONO = 'var(--font-mono, ui-monospace, SFMono-Regular, Menlo, monospace)';
const SERIF = '"Source Serif 4", "Source Serif Pro", Georgia, serif';
const NO_MODULE = '__none__';
const LANE_KEY = 'itms-qc:dash-lane';
const SNAPSHOT_KEY = 'itms-qc:dash-snapshot';

type Lane = 'bug' | 'feature_request';
type TileKey = 'needsReply' | 'accepted' | 'inProgress' | 'fixed' | 'closed';

function loadLane(): Lane {
  try {
    const raw = localStorage.getItem(LANE_KEY);
    if (raw === 'bug' || raw === 'feature_request') return raw;
  } catch {
    /* default below */
  }
  return 'bug';
}

function greeting(): string {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 18) return 'Good afternoon';
  return 'Good evening';
}

function moduleKey(c: QcCase): string {
  return c.moduleSlug ?? NO_MODULE;
}

const TERMINAL = QC_TERMINAL_STATUSES as readonly string[];

/** Fold a fresh case into the dashboard payload, moving it between the open and closed lists as its status dictates. */
function mergeCase(data: QcDashboard, fresh: QcCase): QcDashboard {
  const strip = (list: QcCase[]) => list.filter((c) => c.id !== fresh.id);
  const wasOpen = data.openCases.some((c) => c.id === fresh.id);
  const wasClosed = data.closedCases.some((c) => c.id === fresh.id);
  const terminal = TERMINAL.includes(fresh.status);
  const open = terminal ? strip(data.openCases) : wasOpen ? data.openCases.map((c) => (c.id === fresh.id ? fresh : c)) : wasClosed ? [fresh, ...data.openCases] : data.openCases;
  const closed = terminal ? (wasClosed ? data.closedCases.map((c) => (c.id === fresh.id ? fresh : c)) : [fresh, ...data.closedCases]) : strip(data.closedCases);
  const my = data.myCases.map((c) => (c.id === fresh.id ? fresh : c));
  return { ...data, openCases: open, closedCases: closed, myCases: my };
}

export function DashboardPage(): React.ReactElement {
  const getToken = useApiToken();
  const me = useMe();
  const meOid = me?.oid ?? null;
  const navigate = useNavigate();
  const canDock = useCanDock();
  const { instance, accounts } = useMsal();
  // The ACTIVE account, never simply the first cached (auth/ssoMemory.ts).
  const snapshotKey = `${SNAPSHOT_KEY}:${signedInAccount(instance, accounts)?.localAccountId ?? 'anon'}`;

  const [data, setData] = useState<QcDashboard | null>(() => readSnapshot<QcDashboard>(snapshotKey)?.value ?? null);
  const [live, setLive] = useState(false); // false while showing the snapshot
  const [error, setError] = useState<string | null>(null);
  const [tile, setTile] = useState<TileKey>('needsReply');
  const [lane, setLane] = useState<Lane>(loadLane);
  const [moduleFilter, setModuleFilter] = useState<string>('all');
  const [query, setQuery] = useState('');

  // The docked case lives in the URL (?case=171) so refresh and back work.
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedId = Number(searchParams.get('case')) || null;
  const select = useCallback(
    (id: number | null, replace = false) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (id) next.set('case', String(id));
          else next.delete('case');
          return next;
        },
        { replace },
      );
    },
    [setSearchParams],
  );

  // New case (the button used to live only on the cases page).
  const [showNewCase, setShowNewCase] = useState(false);
  const [modules, setModules] = useState<QcModule[] | null>(null);

  const load = useCallback(async () => {
    try {
      const token = await getToken();
      const d = await api.dashboard(token);
      setData(d);
      setLive(true);
      setError(null);
      writeSnapshot(snapshotKey, d);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load dashboard');
    }
  }, [getToken, snapshotKey]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    try {
      localStorage.setItem(LANE_KEY, lane);
    } catch {
      /* lane just doesn't persist */
    }
  }, [lane]);

  // Modules only when the form is first opened — not part of the landing cost.
  useEffect(() => {
    if (!showNewCase || modules) return;
    void (async () => {
      try {
        const token = await getToken();
        const { modules: list } = await api.listModules(token);
        setModules(list);
      } catch (err) {
        console.error('[dashboard] failed to load modules:', err);
      }
    })();
  }, [showNewCase, modules, getToken]);

  // === Derived lists ===
  const derived = useMemo(() => {
    if (!data) return null;
    const inLane = (c: QcCase) => c.type === lane;
    const laneOpen = data.openCases.filter(inLane);
    const modMap = new Map<string, { slug: string | null; name: string | null; count: number }>();
    for (const c of laneOpen) {
      const k = moduleKey(c);
      const e = modMap.get(k) ?? { slug: c.moduleSlug, name: c.moduleName, count: 0 };
      e.count += 1;
      modMap.set(k, e);
    }
    const moduleChips = [...modMap.entries()].map(([key, m]) => ({ key, ...m })).sort((a, b) => b.count - a.count);
    const inModule = (c: QcCase) => moduleFilter === 'all' || moduleKey(c) === moduleFilter;
    const moduleCases = laneOpen.filter(inModule);
    const closedCases = data.closedCases.filter(inLane).filter(inModule);
    const byTile: Record<TileKey, QcCase[]> = {
      needsReply: moduleCases.filter((c) => conversationCourt(c) === 'awaiting_dev'),
      accepted: moduleCases.filter((c) => c.status === 'accepted'),
      inProgress: moduleCases.filter((c) => c.status === 'in_progress'),
      fixed: moduleCases.filter((c) => c.status === 'fixed'),
      closed: closedCases,
    };
    const q = query.trim().toLowerCase();
    const filtered = q ? byTile[tile].filter((c) => c.title.toLowerCase().includes(q) || c.caseNumber.toLowerCase().includes(q)) : byTile[tile];
    const openBugs = data.openCases.filter((c) => c.type === 'bug').length;
    const openFeats = data.openCases.length - openBugs;
    const all = [...data.openCases, ...data.closedCases, ...data.myCases];
    return { laneOpen, moduleChips, byTile, filtered, openBugs, openFeats, all };
  }, [data, lane, moduleFilter, tile, query]);

  // Open on work: select the first row when nothing is selected, or when the
  // selection belongs to the other lane. Never yank a selection the person
  // made that merely fell out of the current filter.
  useEffect(() => {
    if (!canDock || !derived) return;
    const current = selectedId ? derived.all.find((c) => c.id === selectedId) : undefined;
    const wrongLane = current ? current.type !== lane : false;
    if ((!selectedId || wrongLane) && derived.filtered.length > 0) {
      select(derived.filtered[0].id, true);
    }
  }, [canDock, derived, selectedId, lane, select]);

  // ↑/↓ walk the active list, Esc closes the pane — unless typing somewhere.
  useEffect(() => {
    if (!canDock || !derived) return;
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable)) return;
      const list = derived.filtered;
      if (e.key === 'Escape' && selectedId) {
        e.preventDefault();
        select(null);
        return;
      }
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
      if (list.length === 0) return;
      e.preventDefault();
      const idx = list.findIndex((c) => c.id === selectedId);
      const next = e.key === 'ArrowDown' ? Math.min(idx + 1, list.length - 1) : Math.max(idx - 1, 0);
      select(list[next].id);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [canDock, derived, selectedId, select]);

  const onCaseChanged = useCallback((fresh: QcCase) => {
    setData((prev) => (prev ? mergeCase(prev, fresh) : prev));
  }, []);

  const firstName = (me?.displayName ?? '').split(' ')[0];
  const docked = canDock && !!selectedId;
  const selectedRow = selectedId && derived ? derived.all.find((c) => c.id === selectedId) ?? null : null;

  const needsReplyLabel = lane === 'bug' ? 'Needs your reply' : 'To review';
  const listTitle: Record<TileKey, string> = {
    needsReply: lane === 'bug' ? 'Bugs needing your reply' : 'Feature requests to review',
    accepted: 'Accepted — backlog',
    inProgress: 'In progress',
    fixed: lane === 'bug' ? 'Fixed — awaiting verify' : 'Built — awaiting verify',
    closed: lane === 'bug' ? 'Closed bugs' : 'Closed feature requests',
  };

  function openRow(c: QcCase) {
    if (canDock) select(c.id);
    else navigate(`/cases/${c.id}`);
  }

  return (
    <div>
      {/* Page header */}
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', marginBottom: 14 }}>
        <div>
          <div style={{ fontFamily: SERIF, fontSize: 24, fontWeight: 600, letterSpacing: '-0.01em', color: 'var(--brand-text, #001C3E)', lineHeight: 1.2 }}>
            {greeting()}
            {firstName ? `, ${firstName}` : ''}
          </div>
          <div style={{ fontSize: 13, color: 'var(--brand-text-muted, #8BA4B8)', marginTop: 3 }}>
            {derived
              ? `${derived.openBugs} open bug${derived.openBugs === 1 ? '' : 's'} · ${derived.openFeats} feature request${derived.openFeats === 1 ? '' : 's'} across WISE${live ? '' : ' · refreshing…'}`
              : 'Quality control across all WISE modules'}
          </div>
        </div>
        <button type="button" onClick={() => setShowNewCase(true)} style={{ ...primaryButtonStyle, fontSize: 13, padding: '7px 14px' }}>
          + New case
        </button>
      </div>

      {error && (
        <div style={{ background: '#FEE', color: '#900', padding: 10, borderRadius: 6, marginBottom: 16, fontSize: 13 }}>{error}</div>
      )}

      <div
        className="qc-workspace"
        style={{
          display: 'grid',
          gridTemplateColumns: docked ? 'minmax(360px, 410px) minmax(0, 1fr)' : 'minmax(0, 1fr)',
          gap: 16,
          alignItems: 'start',
        }}
      >
        {/* === Queue === */}
        <div style={{ background: 'var(--brand-card-bg, white)', border: '1px solid var(--brand-border, #D6E4EE)', borderRadius: 8, overflow: 'hidden', minWidth: 0 }}>
          {/* Lane switch */}
          <div role="tablist" aria-label="Case type lane" style={{ display: 'flex', borderBottom: '1px solid var(--brand-border, #D6E4EE)' }}>
            {(
              [
                { v: 'bug', label: '🐛 Bugs', n: derived?.openBugs },
                { v: 'feature_request', label: '✨ Feature requests', n: derived?.openFeats },
              ] as { v: Lane; label: string; n: number | undefined }[]
            ).map((t) => {
              const active = lane === t.v;
              return (
                <button
                  key={t.v}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => {
                    setLane(t.v);
                    if (t.v === 'bug' && tile === 'accepted') setTile('needsReply');
                  }}
                  style={{
                    flex: 1,
                    border: 'none',
                    borderBottom: `2px solid ${active ? 'var(--brand-interactive, #2F6CB5)' : 'transparent'}`,
                    marginBottom: -1,
                    padding: '10px 12px',
                    fontSize: 13,
                    fontWeight: active ? 600 : 500,
                    cursor: 'pointer',
                    background: 'transparent',
                    color: active ? 'var(--brand-primary, #001C3E)' : 'var(--brand-text-muted, #5A6B7B)',
                  }}
                >
                  {t.label}
                  {typeof t.n === 'number' && <span style={{ marginLeft: 6, fontVariantNumeric: 'tabular-nums', opacity: 0.75 }}>{t.n}</span>}
                </button>
              );
            })}
          </div>

          {/* Action pills — counts you can click, not tiles to admire. */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, padding: '10px 12px 6px' }}>
            <Pill label={needsReplyLabel} count={derived?.byTile.needsReply.length} active={tile === 'needsReply'} onClick={() => setTile('needsReply')} accent="#8A5A00" />
            {lane === 'feature_request' && (
              <Pill label="Accepted" count={derived?.byTile.accepted.length} active={tile === 'accepted'} onClick={() => setTile('accepted')} accent="#055560" />
            )}
            <Pill label="In progress" count={derived?.byTile.inProgress.length} active={tile === 'inProgress'} onClick={() => setTile('inProgress')} />
            <Pill label="Awaiting verify" count={derived?.byTile.fixed.length} active={tile === 'fixed'} onClick={() => setTile('fixed')} accent="#1B7A3D" />
            <Pill label="Closed" count={derived?.byTile.closed.length} active={tile === 'closed'} onClick={() => setTile('closed')} subdued />
          </div>

          {/* Module chips + search */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', padding: '4px 12px 10px', borderBottom: '1px solid var(--brand-border, #D6E4EE)' }}>
            {derived && derived.moduleChips.length > 1 && (
              <>
                <ModuleChip label="All" count={derived.laneOpen.length} active={moduleFilter === 'all'} onClick={() => setModuleFilter('all')} />
                {derived.moduleChips.map((m) => (
                  <ModuleChip
                    key={m.key}
                    label={m.name ?? m.slug ?? 'No module'}
                    count={m.count}
                    slug={m.slug}
                    active={moduleFilter === m.key}
                    onClick={() => setModuleFilter(moduleFilter === m.key ? 'all' : m.key)}
                  />
                ))}
              </>
            )}
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search this list…"
              aria-label="Search cases"
              style={{
                flex: 1,
                minWidth: 120,
                fontSize: 12,
                padding: '5px 9px',
                border: '1px solid var(--brand-border, #D6E4EE)',
                borderRadius: 14,
                color: 'var(--brand-text, #001C3E)',
                background: 'var(--brand-bg, #F4F8FB)',
              }}
            />
          </div>

          {/* Rows */}
          <div style={{ padding: '8px 12px 4px', fontSize: 11, fontWeight: 600, color: 'var(--brand-text-muted, #8BA4B8)' }}>
            {derived ? `${listTitle[tile]} · ${derived.filtered.length}` : 'Loading your board…'}
          </div>
          {!derived && (
            <div aria-hidden style={{ padding: '4px 12px 14px', display: 'flex', flexDirection: 'column', gap: 10 }}>
              {[0, 1, 2, 3, 4].map((i) => (
                <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <span style={{ height: 11, width: `${70 - i * 6}%`, borderRadius: 4, background: 'var(--brand-border, #D6E4EE)', opacity: 0.6 }} />
                  <span style={{ height: 9, width: '40%', borderRadius: 4, background: 'var(--brand-border, #D6E4EE)', opacity: 0.4 }} />
                </div>
              ))}
            </div>
          )}
          {derived && derived.filtered.length === 0 && (
            <div style={{ padding: '18px 12px 22px', fontSize: 13, color: 'var(--brand-text-muted, #8BA4B8)' }}>
              {query ? 'No cases match your search.' : tile === 'needsReply' ? 'Nothing is waiting on you.' : 'Nothing here.'}
            </div>
          )}
          {derived && derived.filtered.length > 0 && (
            <div role="list">
              {derived.filtered.map((c) => (
                <QueueRow
                  key={c.id}
                  c={c}
                  selected={docked && c.id === selectedId}
                  meOid={meOid}
                  selfName={me?.displayName}
                  people={data?.people ?? {}}
                  showModule={moduleFilter === 'all'}
                  onClick={() => openRow(c)}
                />
              ))}
            </div>
          )}
        </div>

        {/* === Docked case === */}
        {docked && selectedId && (
          <CaseView key={selectedId} caseId={selectedId} variant="pane" seed={selectedRow} onClose={() => select(null)} onChanged={onCaseChanged} />
        )}
      </div>

      {showNewCase && (
        <NewCaseModal
          modules={modules ?? []}
          onClose={() => setShowNewCase(false)}
          onCreated={(caseId) => {
            setShowNewCase(false);
            void load();
            if (canDock) select(caseId);
            else navigate(`/cases/${caseId}`);
          }}
          onAttached={(caseId) => {
            setShowNewCase(false);
            void load();
            if (canDock) select(caseId);
            else navigate(`/cases/${caseId}`);
          }}
        />
      )}
    </div>
  );
}

function Pill({ label, count, active, onClick, accent, subdued }: { label: string; count: number | undefined; active: boolean; onClick: () => void; accent?: string; subdued?: boolean }) {
  const color = accent ?? 'var(--brand-interactive, #2F6CB5)';
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className="qc-pill"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        fontSize: 12,
        fontWeight: active ? 600 : 500,
        padding: '5px 10px',
        borderRadius: 6,
        cursor: 'pointer',
        border: `1px solid ${active ? color : 'var(--brand-border, #D6E4EE)'}`,
        background: active ? 'var(--brand-card-bg, white)' : 'transparent',
        boxShadow: active ? `inset 0 0 0 1px ${color}` : 'none',
        color: active ? 'var(--brand-text, #001C3E)' : 'var(--brand-text-muted, #5A6B7B)',
        opacity: subdued && !active ? 0.75 : 1,
      }}
    >
      <span style={{ width: 7, height: 7, borderRadius: '50%', background: color, flexShrink: 0, opacity: active ? 1 : 0.55 }} />
      {label}
      <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700, color: 'var(--brand-text, #001C3E)' }}>{typeof count === 'number' ? count : '–'}</span>
    </button>
  );
}

function ModuleChip({ label, count, slug, active, onClick }: { label: string; count: number; slug?: string | null; active: boolean; onClick: () => void }) {
  const dot = slug !== undefined ? moduleColors(slug).bg : null;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        fontSize: 11,
        fontWeight: 500,
        padding: '3px 9px',
        borderRadius: 14,
        cursor: 'pointer',
        border: `1px solid ${active ? 'var(--brand-interactive, #2F6CB5)' : 'var(--brand-border, #D6E4EE)'}`,
        background: active ? 'var(--brand-interactive, #2F6CB5)' : 'var(--brand-card-bg, white)',
        color: active ? 'white' : 'var(--brand-text, #001C3E)',
      }}
    >
      {dot && <span style={{ width: 7, height: 7, borderRadius: '50%', background: dot, flexShrink: 0 }} />}
      {label}
      <span style={{ fontVariantNumeric: 'tabular-nums', opacity: 0.7 }}>{count}</span>
    </button>
  );
}

function QueueRow({ c, selected, meOid, selfName, people, showModule, onClick }: { c: QcCase; selected: boolean; meOid: string | null; selfName?: string | null; people: QcPeopleMap; showModule?: boolean; onClick: () => void }) {
  const unread = isUnread(c.id, c.updatedAt);
  return (
    <div
      onClick={onClick}
      role="listitem"
      tabIndex={0}
      aria-current={selected ? 'true' : undefined}
      onKeyDown={(e) => e.key === 'Enter' && onClick()}
      className="qc-queue-row"
      style={{
        padding: '9px 12px 9px 9px',
        borderBottom: '1px solid var(--brand-bg, #F4F8FB)',
        borderLeft: `3px solid ${selected ? 'var(--brand-interactive, #2F6CB5)' : 'transparent'}`,
        background: selected ? 'var(--brand-bg, #F4F8FB)' : 'transparent',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
      }}
    >
      <span
        aria-label={unread ? 'Unread activity' : undefined}
        title={unread ? 'Activity since you last opened this case' : undefined}
        style={{ width: 7, height: 7, borderRadius: '50%', background: unread ? 'var(--brand-interactive, #2F6CB5)' : 'transparent', flexShrink: 0 }}
      />
      <TypeIcon type={c.type} size={14} />
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 10, color: 'var(--brand-text-muted, #8BA4B8)', fontFamily: MONO, whiteSpace: 'nowrap', flexShrink: 0 }}>{c.caseNumber}</span>
          <span style={{ fontSize: 13, fontWeight: unread ? 700 : 500, color: 'var(--brand-text, #001C3E)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.title}</span>
        </div>
        <div style={{ fontSize: 11, color: 'var(--brand-text-muted, #8BA4B8)', marginTop: 2, display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
          {showModule && <ModuleBadge slug={c.moduleSlug} name={c.moduleName} />}
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {personLabel(c.openedBy, meOid, people, { selfName })} · {relativeTime(c.lastNoteAt ?? c.createdAt)}
            {c.noteCount > 0 && ` · 💬 ${c.noteCount}`}
          </span>
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 3, flexShrink: 0 }}>
        <StatusPill status={c.status} />
        <CourtChip court={conversationCourt(c)} />
      </div>
    </div>
  );
}
